/**
 * pushNotifications.js
 * Registers the device with FCM (via Capacitor) and posts the token to the
 * backend so it can be used as a push target. No-op on web — only runs when
 * the app is running natively (Capacitor APK).
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import api from '../services/api';

let registered = false;

export async function initPushNotifications() {
  if (registered) return;
  if (!Capacitor.isNativePlatform || !Capacitor.isNativePlatform()) {
    // Browser / desktop — push only works inside the Android app.
    return;
  }
  registered = true;

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      console.warn('[push] permission not granted');
      return;
    }

    // Create a high-importance channel with our custom sound. Channel sound is
    // locked in at creation time on the device — to change the sound later,
    // bump the channel id (e.g. 'pv-approvals-v2').
    try {
      await PushNotifications.createChannel({
        id: 'pv-approvals-v2',
        name: 'PV Approvals',
        description: 'Payment voucher approval alerts',
        importance: 5,
        visibility: 1,
        sound: 'pv_alret',
        vibration: true,
        lights: true,
      });
      // Separate channel for member contribution-due reminders (same sound for now).
      await PushNotifications.createChannel({
        id: 'contributions-v1',
        name: 'Contributions',
        description: 'Reminders when your contribution is due',
        importance: 5,
        visibility: 1,
        sound: 'pv_alret',
        vibration: true,
        lights: true,
      });
    } catch (e) {
      console.warn('[push] createChannel failed:', e?.message);
    }

    await PushNotifications.register();

    PushNotifications.addListener('registration', async (token) => {
      try {
        await api.post('/push/register', { token: token.value, platform: 'android' });
      } catch (e) {
        console.warn('[push] failed to send token to backend:', e?.message);
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] registration error:', err);
    });

    // Foreground notifications — Android still shows them, but we could also surface a toast.
    PushNotifications.addListener('pushNotificationReceived', (n) => {
      console.log('[push] received in foreground:', n);
    });

    // When the user taps the notification.
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data || {};
      const go = (path) => {
        window.location.hash = '';
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      };
      if (data.type === 'cr-pending') {
        go('/finance/cr');
      } else if (data.type && data.type.startsWith('pv-')) {
        go('/finance/pv');
      } else if (data.type === 'contribution-due') {
        go('/member/subscriptions');
      } else if (data.type === 'father-assigned') {
        go('/member/my-members');
      }
    });
  } catch (e) {
    console.warn('[push] init failed:', e?.message);
  }
}

export async function unregisterPushNotifications() {
  if (!Capacitor.isNativePlatform || !Capacitor.isNativePlatform()) return;
  // We don't have direct token access here — the backend route DELETE /push/register
  // without a token clears all of the user's tokens.
  try {
    await api.delete('/push/register');
  } catch (_) {}
}
